export class DPoPManager {
  constructor() {
    this.keyPair = null;
    this.publicKeyJWK = null;
    this.sessionId = null;
    this.restoredFromStorage = false;
    this.dbName = 'frs_dpop';
    this.storeName = 'keys';
    this.keyId = 'session_key';
  }

  async initialize() {
    const storedKeyPair = await this.loadKeyPair();
    if (storedKeyPair) {
      this.keyPair = storedKeyPair;
      this.publicKeyJWK = await crypto.subtle.exportKey('jwk', this.keyPair.publicKey);
      this.restoredFromStorage = true;
    } else {
      await this.generateKeyPair();
      await this.storeKeyPair();
      this.restoredFromStorage = false;
    }
    this.sessionId = crypto.randomUUID();
  }

  openDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onupgradeneeded = () => {
        request.result.createObjectStore(this.storeName);
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async loadKeyPair() {
    try {
      const db = await this.openDatabase();
      return await new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readonly');
        const request = transaction.objectStore(this.storeName).get(this.keyId);

        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => db.close();
      });
    } catch {
      return null;
    }
  }

  async storeKeyPair() {
    try {
      const db = await this.openDatabase();
      await new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        transaction.objectStore(this.storeName).put(this.keyPair, this.keyId);

        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
        transaction.onerror = () => reject(transaction.error);
      });
    } catch {
      // If IndexedDB is blocked, the in-memory key still works for this tab.
    }
  }

  async deleteStoredKeyPair() {
    try {
      const db = await this.openDatabase();
      await new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        transaction.objectStore(this.storeName).delete(this.keyId);

        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
        transaction.onerror = () => reject(transaction.error);
      });
    } catch {
      // Local logout should not fail if browser storage cleanup is unavailable.
    }
  }

  async generateKeyPair() {
    this.keyPair = await crypto.subtle.generateKey(
      {
        name: 'RSASSA-PKCS1-v1_5',
        modulusLength: 2048,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: 'SHA-256'
      },
      false,
      ['sign', 'verify']
    );

    this.publicKeyJWK = await crypto.subtle.exportKey('jwk', this.keyPair.publicKey);
  }

  base64UrlEncode(str) {
    return btoa(String.fromCharCode(...new Uint8Array(str)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  async generateDPoPProof(method, url, accessToken = null) {
    if (!this.keyPair) {
      await this.initialize();
    }

    const header = {
      typ: 'dpop+jwt',
      alg: 'RS256',
      jwk: {
        kty: this.publicKeyJWK.kty,
        n: this.publicKeyJWK.n,
        e: this.publicKeyJWK.e
      }
    };

    const payload = {
      jti: crypto.randomUUID(),
      htm: method,
      htu: url,
      iat: Math.floor(Date.now() / 1000)
    };

    if (accessToken) {
      const encoder = new TextEncoder();
      const data = encoder.encode(accessToken);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      payload.ath = this.base64UrlEncode(hashBuffer);
    }

    const encodedHeader = this.base64UrlEncode(
      new TextEncoder().encode(JSON.stringify(header))
    );
    const encodedPayload = this.base64UrlEncode(
      new TextEncoder().encode(JSON.stringify(payload))
    );

    const signatureInput = `${encodedHeader}.${encodedPayload}`;
    const signature = await crypto.subtle.sign(
      'RSASSA-PKCS1-v1_5',
      this.keyPair.privateKey,
      new TextEncoder().encode(signatureInput)
    );

    const encodedSignature = this.base64UrlEncode(signature);
    return `${signatureInput}.${encodedSignature}`;
  }

  clearKeys() {
    this.keyPair = null;
    this.publicKeyJWK = null;
    this.sessionId = null;
    this.restoredFromStorage = false;
    this.deleteStoredKeyPair();
  }

  isInitialized() {
    return this.keyPair !== null;
  }

  wasRestoredFromStorage() {
    return this.restoredFromStorage;
  }
}
